import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Play, Clock, Mic } from "lucide-react";
import { fetchEpisodes } from "../lib/api";
import { formatDate } from "../lib/brand";
import SEO from "../components/SEO";

export default function Episodes() {
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 100 };
    if (debouncedQ.trim()) params.q = debouncedQ.trim();
    fetchEpisodes(params)
      .then((r) => setEpisodes(r.episodes || []))
      .finally(() => setLoading(false));
  }, [debouncedQ]);

  const latest = episodes[0];

  return (
    <div data-testid="episodes-page">
      <SEO
        title="Episodes"
        description="Every Unfiltered & On-Ball episode — raw takes, real perspective, from two former players. Listen on Spotify, watch on YouTube."
        image={latest?.hero_image}
      />

      {/* Header */}
      <section className="border-b-2 border-[var(--ink)]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-12 md:py-16">
          <div className="text-kicker">The Podcast</div>
          <h1 className="text-headline text-5xl md:text-7xl lg:text-8xl mt-2">
            Every <span className="text-[var(--rust)]">Episode</span>.<br />
            On Tape. On Tap.
          </h1>
          <p className="text-dek text-lg md:text-xl mt-4 max-w-2xl">
            New drop every Monday. Listen on Spotify, watch on YouTube, argue with us anywhere.
          </p>
        </div>
      </section>

      {/* Latest hero */}
      {latest && (
        <section className="border-b-2 border-[var(--ink)] py-12" data-testid="latest-episode-hero">
          <div className="max-w-[1400px] mx-auto px-5 md:px-8">
            <Link to={`/episodes/${latest.slug}`} className="grid md:grid-cols-5 gap-8 group">
              <div className="md:col-span-3 overflow-hidden border-2 border-[var(--ink)] bg-[var(--ink)] aspect-[16/10]">
                <img src={latest.hero_image || "https://images.unsplash.com/photo-1607627000458-210e8d2bdb1d?w=1600&q=80"}
                     alt={latest.title} loading="eager"
                     className="w-full h-full object-cover group-hover:scale-[1.03] transition duration-700" />
              </div>
              <div className="md:col-span-2">
                <span className="tape">Latest · Ep. {latest.number}</span>
                <h2 className="text-headline text-3xl md:text-5xl mt-4 group-hover:text-[var(--rust)] transition">
                  {latest.title}
                </h2>
                <p className="text-dek mt-4 text-base md:text-lg">{latest.description}</p>
                <div className="mt-5 flex items-center gap-4 text-xs font-mono-ed uppercase tracking-widest text-[var(--muted)]">
                  <span className="inline-flex items-center gap-1"><Clock size={12} /> {latest.duration}</span>
                  <span>·</span>
                  <span>{formatDate(latest.publish_date)}</span>
                </div>
                <div className="mt-6">
                  <span className="btn-rust">
                    <Play size={14} fill="currentColor" /> Play Episode
                  </span>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Search */}
      <section className="sticky top-[73px] md:top-[85px] z-30 bg-[var(--paper)] border-b-2 border-[var(--ink)]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-4 flex items-center gap-3 justify-between">
          <div className="flex items-center gap-2 font-display uppercase tracking-widest text-sm">
            <Mic size={16} className="text-[var(--rust)]" /> Archive
          </div>
          <div className="flex items-center gap-2 border-2 border-[var(--ink)] px-3 py-2 bg-[var(--paper)] w-64 md:w-96">
            <Search size={16} className="text-[var(--muted)]" />
            <input type="text" placeholder="Search episodes, guests, topics…"
                   value={q} onChange={(e) => setQ(e.target.value)}
                   className="flex-1 bg-transparent outline-none text-sm"
                   data-testid="episodes-search-input" />
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-12" data-testid="episodes-grid">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          {loading ? (
            <div className="text-center py-20 font-mono-ed uppercase tracking-widest text-[var(--muted)]">Loading…</div>
          ) : episodes.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-headline text-3xl">No episodes match.</div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
              {episodes.map((e) => (
                <Link key={e.slug} to={`/episodes/${e.slug}`}
                      className="article-card block group"
                      data-testid={`episode-card-${e.slug}`}>
                  <div className="overflow-hidden border-2 border-[var(--ink)] bg-[var(--ink)] aspect-[16/10] relative">
                    <img src={e.hero_image} alt={e.title} loading="lazy"
                         className="w-full h-full object-cover" />
                    <div className="absolute top-3 left-3 tape text-[0.7rem]">Ep. {e.number}</div>
                    <div className="absolute bottom-3 right-3 bg-[var(--ink)] text-[var(--paper)] px-2 py-1 text-[0.65rem] font-mono-ed uppercase tracking-widest">
                      {e.duration}
                    </div>
                  </div>
                  <div className="pt-4">
                    <div className="text-kicker mb-2">{formatDate(e.publish_date)}</div>
                    <h3 className="text-headline text-2xl art-title text-[var(--ink)]">{e.title}</h3>
                    <p className="text-dek mt-2 text-base line-clamp-2">{e.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
